import { useCallback } from "react";

// Storage utility to handle localStorage safely
const getStorage = () => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
  } catch {}
  return {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
  };
};

// Token expiration caching strategy:
// Cache the parsed expiration timestamp in memory to avoid:
// 1. Repeated string parsing ("15m" -> milliseconds)
// 2. Repeated localStorage reads on every request
// This is critical for performance since isTokenExpired() is called on EVERY authenticated request
let cachedTokenExpiresAt = null;

// Helper function to parse expiresIn value
// Called ONLY when setting tokens (login/refresh), NOT on every request
const parseExpiresIn = (expiresIn) => {
  if (typeof expiresIn === "number") {
    return expiresIn * 1000; // Convert seconds to milliseconds
  }

  const match = expiresIn.match(/^(\d+)([smhd])$/);
  if (match) {
    const value = parseInt(match[1], 10);
    const unit = match[2];

    switch (unit) {
      case "s":
        return value * 1000; // seconds
      case "m":
        return value * 60 * 1000; // minutes
      case "h":
        return value * 60 * 60 * 1000; // hours
      case "d":
        return value * 24 * 60 * 60 * 1000; // days
      default:
        return 15 * 60 * 1000; // Default: 15 minutes
    }
  }

  return 15 * 60 * 1000; // Default: 15 minutes
};

// Helper function to decode JWT payload
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
};

// Non-React utility functions for use in services
export const tokenUtils = {
  getAccessToken: () => {
    return getStorage().getItem("accessToken");
  },

  setAccessToken: (token, expiresIn = null) => {
    if (!token) return;

    getStorage().setItem("accessToken", token);

    if (expiresIn) {
      // Parse expiration string ONCE and cache the timestamp
      const expiresInMs = parseExpiresIn(expiresIn);
      const tokenExpiresAt = Date.now() + expiresInMs;

      // Update both localStorage (for persistence) and memory cache (for performance)
      getStorage().setItem("tokenExpiresAt", String(tokenExpiresAt));
      cachedTokenExpiresAt = tokenExpiresAt;
    }
  },

  getRefreshToken: () => {
    return getStorage().getItem("refreshToken");
  },

  setRefreshToken: (token) => {
    if (!token) return;
    getStorage().setItem("refreshToken", token);
  },

  clearTokens: () => {
    getStorage().removeItem("accessToken");
    getStorage().removeItem("refreshToken");
    getStorage().removeItem("tokenExpiresAt");

    // Clear memory cache
    cachedTokenExpiresAt = null;
  },

  isTokenExpired: () => {
    // Use cached timestamp if available (fast path - no localStorage access!)
    // This is called on EVERY authenticated request, so performance is critical
    if (cachedTokenExpiresAt === null) {
      // Cache miss: load from localStorage and cache it
      const storedExpiresAt = getStorage().getItem("tokenExpiresAt");
      if (!storedExpiresAt) return true;

      cachedTokenExpiresAt = parseInt(storedExpiresAt, 10);
    }

    // Simple timestamp comparison - no parsing needed!
    // Consider token expired 60 seconds before actual expiration
    const REFRESH_BUFFER_MS = 60000;
    return Date.now() > cachedTokenExpiresAt - REFRESH_BUFFER_MS;
  },

  getTokenPayload: () => {
    const token = getStorage().getItem("accessToken");
    if (!token) return null;

    return decodeJWT(token);
  },

  setTokens: (tokens) => {
    if (tokens.accessToken) {
      tokenUtils.setAccessToken(tokens.accessToken, tokens.expiresIn);
    }
    if (tokens.refreshToken) {
      tokenUtils.setRefreshToken(tokens.refreshToken);
    }
  },
};

// React hook for token management
export const useTokenStorage = () => {
  const getAccessToken = useCallback(() => {
    return tokenUtils.getAccessToken();
  }, []);

  const setAccessToken = useCallback((token, expiresIn = null) => {
    tokenUtils.setAccessToken(token, expiresIn);
  }, []);

  const getRefreshToken = useCallback(() => {
    return tokenUtils.getRefreshToken();
  }, []);

  const setRefreshToken = useCallback((token) => {
    tokenUtils.setRefreshToken(token);
  }, []);

  const clearTokens = useCallback(() => {
    tokenUtils.clearTokens();
  }, []);

  const isTokenExpired = useCallback(() => {
    return tokenUtils.isTokenExpired();
  }, []);

  const getTokenPayload = useCallback(() => {
    return tokenUtils.getTokenPayload();
  }, []);

  const setTokens = useCallback((tokens) => {
    tokenUtils.setTokens(tokens);
  }, []);

  return {
    getAccessToken,
    setAccessToken,
    getRefreshToken,
    setRefreshToken,
    clearTokens,
    isTokenExpired,
    getTokenPayload,
    setTokens,
  };
};
